/**
 * Форма обмена ✦ → ☾ на экране кошелька (§Э1.2, §Т2.4).
 *
 * Назначение файла: явное подтверждение списания кассы перед POST /wallet/convert.
 * Курс берётся из @hive/shared, не дублируется в клиенте.
 */

import { formatNumber, type LocaleCode } from "@hive/i18n";
import { WALLET } from "@hive/shared";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { CrystalAmount } from "./CrystalIcon.jsx";

export interface ConvertPanelProps {
  readonly locale: LocaleCode;
  readonly cashCrystals: number;
  readonly busy?: boolean;
  readonly onConvert: (amount: number) => Promise<void>;
}

export function ConvertPanel({
  locale,
  cashCrystals,
  busy = false,
  onConvert,
}: ConvertPanelProps): React.JSX.Element | null {
  const { t } = useTranslation("economy");
  const [amountText, setAmountText] = useState("1");
  const [confirmed, setConfirmed] = useState(false);

  const parsedAmount = useMemo(() => {
    const value = Number.parseInt(amountText, 10);
    return Number.isFinite(value) ? value : 0;
  }, [amountText]);

  const hiveGain = parsedAmount * WALLET.convertRate;
  const amountValid = parsedAmount > 0 && parsedAmount <= cashCrystals;
  const canSubmit = amountValid && confirmed && !busy;

  if (cashCrystals <= 0) {
    return (
      <section className="convert-panel" aria-label={t("convert.title")}>
        <h2 className="screen__section-title">{t("convert.title")}</h2>
        <p className="convert-panel__empty">{t("convert.empty")}</p>
      </section>
    );
  }

  const handleSubmit = (): void => {
    if (!canSubmit) return;
    void onConvert(parsedAmount).then(() => {
      setConfirmed(false);
      setAmountText("1");
    });
  };

  return (
    <section className="convert-panel" aria-label={t("convert.title")}>
      <h2 className="screen__section-title">{t("convert.title")}</h2>
      <p className="convert-panel__rate">{t("convert.rate", { rate: WALLET.convertRate })}</p>
      <p className="convert-panel__hint">{t("convert.hint")}</p>

      <label className="convert-panel__field">
        <span className="convert-panel__field-label">{t("convert.amount_label")}</span>
        <div className="convert-panel__amount-row">
          <input
            className="convert-panel__input"
            type="number"
            inputMode="numeric"
            min={1}
            max={cashCrystals}
            value={amountText}
            disabled={busy}
            onChange={(event) => setAmountText(event.target.value)}
          />
          <button
            type="button"
            className="button button--link convert-panel__max"
            disabled={busy}
            onClick={() => setAmountText(String(cashCrystals))}
          >
            {t("convert.use_max")}
          </button>
        </div>
      </label>

      {amountValid && (
        <p className="convert-panel__preview">
          <CrystalAmount kind="cash" amount={formatNumber(locale, parsedAmount)} />
          <span className="convert-panel__arrow" aria-hidden="true">
            {t("convert.preview_arrow")}
          </span>
          <CrystalAmount kind="hive" amount={formatNumber(locale, hiveGain)} />
        </p>
      )}

      <label className="convert-panel__confirm">
        <input
          type="checkbox"
          checked={confirmed}
          disabled={busy}
          onChange={(event) => setConfirmed(event.target.checked)}
        />
        <span>{t("convert.confirm")}</span>
      </label>

      <button
        type="button"
        className="button button--primary convert-panel__submit"
        disabled={!canSubmit}
        onClick={handleSubmit}
      >
        {t("convert.submit")}
      </button>
    </section>
  );
}
